/**
 * The ONE shape a correspondence file is drawn as, and the rule that decides
 * what goes in its box (#1069 Story 8.5, D1/D3; was two shapes, Story 7.4 D3).
 *
 * ⚠️ THIS FILE EXISTS SO THERE IS ONE ROW, NOT TWO. Both surfaces that draw a
 * message's files -- the bubble (`MessageAttachments`) and the conversation's
 * files strip (`ThreadFilesStrip`) -- render THIS piece. A second copy is
 * exactly how they drift into two families that look almost alike, which 4.2
 * AC 9 forbids and which Story 8.5 exists to undo: it deleted a 92pt image
 * tile, a two-column grid, a `+N` scrim and a bordered document chip, all so
 * that a file on a message looks like a file on the request.
 *
 * ⚠️ AND THE THUMBNAIL IS THE REQUEST'S OWN COMPONENT, NOT A LOOK-ALIKE
 * (D1, the reason it is not negotiable). `AttachmentThumbnail size="xsmall"`
 * is what Supporting Documents draws, so the two lists cannot teach a reader
 * two languages. A re-implementation in the correspondence palette mirrors
 * today and drifts by the second week, and the only test that could catch that
 * drift is one that compares two components -- which the suite writes, cheaply,
 * precisely because the component is shared.
 *
 * ⚠️ IT IS HANDED `source`, NEVER `attachment` (D1a, AC 3). A `MessageFile`'s
 * id is a message-file id and its picture is already on the wire in
 * `thumbnailUri`; through `attachment` the component would fetch one
 * request-attachment thumbnail PER ROW, for a row that does not exist. The
 * suite fails if the row renders the thumbnail without `source`.
 *
 * ⚠️ NOTHING HERE DECIDES ITS OWN WIDTH. The row takes a `style` from its
 * caller: the bubble caps it at `ROW_MAX_WIDTH` because a 255-character file
 * name would otherwise size the bubble, and the strip stretches it because a
 * full-width row IS the thing Story 7.6 jumps from and Story 7.5 hangs a save
 * action beside. A width policy baked in here would be one surface's rule
 * imposed on the other.
 *
 * ⚠️ ONE PRESSABLE PER ROW. The thumbnail gets no `onPress`: React Native has
 * no `stopPropagation`, so a nested press would give the row two meanings, and
 * RNTL walks UP the tree for a handler so the nested case could not even be
 * pinned honestly (`.claude/rules/correspondence.md`, 7.2; Story 7.5 D9).
 *
 * ⚠️ NO `Platform.OS` READ, AT MODULE SCOPE OR ANYWHERE. Story 7.2's lesson
 * (`.claude/rules/correspondence.md`): `setupTests.ts` resolves the platform
 * before any suite's `beforeEach` runs, so a module-scope read freezes the web
 * branch off for the life of the process. This piece has no platform branch by
 * design; if one is ever needed it is read inside the component.
 *
 * @see src/components/correspondence/__tests__/attachmentPieces.test.tsx
 */

import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontWeight } from '../../theme/typography';
import {
  formatFileName,
  formatFileSize,
} from '../../utils/attachmentFormatters';
import { AttachmentThumbnail } from '../attachments/AttachmentThumbnail';

import type { MessageFile } from './correspondence.types';
import type { StyleProp, ViewStyle } from 'react-native';

/**
 * The BUBBLE's width ceiling for a file row, in points.
 *
 * ⚠️ IT IS 188 BECAUSE IT WAS THE GRID'S WIDTH, AND IT IS KEPT BECAUSE OF THE
 * DEFECT IT CLOSES, NOT BECAUSE OF THE GRID (#1069 Story 8.5, C6/D3; was
 * `GRID_WIDTH`, two 92pt tile columns plus a 4 gap). The row list has no
 * definite width to declare, but the cap is still load-bearing: the bubble's
 * attachments container is `alignItems: 'flex-start'`, so without a ceiling
 * the row takes its INTRINSIC width -- and `flex: 1` on the name inside a row
 * with no definite width resolves to the text's own width, so
 * `numberOfLines={1}` truncates nothing at all. `file_name` allows 255
 * characters, so one long name dragged the row and its bubble far past
 * everything beside it. 188 also still sits inside 4.2 C2's binding 193px
 * (320pt Android, other side, after the avatar column and the bubble's
 * padding).
 *
 * ⚠️ THE STRIP PASSES `alignSelf: 'stretch'` INSTEAD, which is why this is a
 * caller's prop and not a style in the piece. Deleting the cap along with the
 * grid it came from re-opens the defect above.
 */
export const ROW_MAX_WIDTH = 188;

/**
 * ⚠️ NOTHING IS DRAWN FROM THIS PARTITION ANY MORE, AND IT IS STILL LOAD-
 * BEARING (#1069 Story 8.5, C5/D4). Story 8.5 draws ONE list in the server's
 * order, so this no longer chooses a shape -- but it is still
 *
 *   (a) the rule for what goes in a row's box (a drawable picture, or the
 *       type glyph), and
 *   (b) THE VIEWER'S INDEX SOURCE. `CorrespondencePanel.openAttachment` takes
 *       an index into `pictures` or into `documents`, because the viewer pages
 *       images as a set and opens a document alone. A row's position in what
 *       the reader SEES is not its position in either list, so
 *       `MessageAttachments` maps back through here.
 *
 * ⚠️ DELETING IT AND INDEXING THE ROW LIST INSTEAD IS THE DEFECT (C5). It is
 * `.claude/rules/correspondence.md`'s BMP lesson in its original shape: a
 * predicate in one place and the index it produces read in another. For
 * `[heic, jpg, pdf]` the rows are 0,1,2 while `pictures` is `[jpg]` and
 * `documents` is `[heic, pdf]` -- pressing row 1 must report 0. A suite that
 * never mixes a thumbnail-less image with other files cannot see the
 * difference, which is exactly how the defect 7.4 D3 fixed survived Story 4.2.
 *
 * ⚠️ AND THE PREDICATE ITSELF DOES NOT MOVE: `kind === 'image' && thumbnailUri`.
 * `kind` is the sniffed type's answer and stays that way -- it is the wire
 * contract, and conditioning it on whether a thumbnail came out would make that
 * contract depend on `sharp` succeeding. The server generates a thumbnail for
 * every image it can decode, so a missing one means it could not read the
 * format. The prompting case is an iPhone photo: HEIC sniffs as an image, and
 * `sharp`'s prebuilt binary carries libheif with AV1 only and no HEVC decoder,
 * so the thumbnail comes back null (measured 2026-09-14 -- "heif: Decoder
 * plugin generated an error"). Such a file gets the generic image glyph in its
 * box, its name, its size and a working open, which is everything we actually
 * know about it.
 *
 * ⚠️ IT IS THE SAME PREDICATE ON BOTH SURFACES, and shared code is what makes
 * that a fact rather than a coincidence: the bubble and the strip cannot
 * disagree about which half a file falls in.
 */
export const partitionForDisplay = <T extends MessageFile>(
  files: readonly T[],
): { pictures: T[]; documents: T[] } => ({
  pictures: files.filter(f => f.kind === 'image' && f.thumbnailUri),
  documents: files.filter(f => !(f.kind === 'image' && f.thumbnailUri)),
});

/**
 * `PDF`, `PNG`, or `FILE` — what a screen reader announces first (4.2 AC 14).
 *
 * ⚠️ THE STORED (SNIFFED) TYPE WINS OVER THE NAME (code review, 2026-09-14).
 * This read the file name alone, which made it the one place in that story
 * that trusted the caller's text over the bytes: a PDF named `deck.pptx` was
 * announced as "PPTX" while the viewer, correctly, rendered it as a PDF. The
 * name is the fallback, not the source.
 *
 * ⚠️ AND AN EXTENSION CAN BE EMPTY. `report.` passes `dot > 0` with an empty
 * slice, so the label opened `", report., 24 KB"` and a reader announced a
 * blank field where the type belongs.
 */
export const extensionLabel = (file: MessageFile): string => {
  const slash = file.mimeType.lastIndexOf('/');
  const subtype = slash >= 0 ? file.mimeType.slice(slash + 1) : '';
  // `application/octet-stream` is what the sniffer stores for anything it did
  // not recognise, and "OCTET-STREAM" tells a person nothing the name would
  // not tell them better.
  if (subtype && subtype !== 'octet-stream' && !subtype.includes('.')) {
    return subtype.toUpperCase();
  }
  const dot = file.name.lastIndexOf('.');
  const fromName = dot > 0 ? file.name.slice(dot + 1).toUpperCase() : '';
  return fromName || 'FILE';
};

type RowProps = {
  file: MessageFile;
  testID: string;
  onPress?: () => void;
  onLongPress?: () => void;
  /**
   * The caller's width policy, and the ONLY thing the two surfaces disagree
   * about here. See the file header and {@link ROW_MAX_WIDTH}.
   */
  style?: StyleProp<ViewStyle>;
  /**
   * What the row announces. Absent, it names itself `TYPE, name, size` -- the
   * formula the chip used, unchanged on every file including photos (Story 8.5
   * D2), so `MessageAttachments` passes nothing and hears no change. The files
   * strip passes a sentence that describes a JUMP (#1069 Story 7.6, D10):
   * there the press does not open the file, and an accessible name that said
   * "PDF, floorplan.pdf, 2.1 MB" would promise the wrong verb.
   */
  accessibilityLabel?: string;
  /**
   * ⚠️ THE MIDDLE TRUNCATOR'S CAP, AND IT IS THE CALLER'S BECAUSE THE TWO
   * SURFACES HAVE DIFFERENT COLUMNS (code review, 2026-09-21). One shared
   * `28` was wrong in BOTH directions and self-defeating in one of them:
   *
   * - In the bubble the name column is ~140 (`ROW_MAX_WIDTH` 188, less the
   *   40 box and the 8 gap). 28 characters at `fontSize: 14` is ~195, so
   *   `numberOfLines={1}`'s TAIL ellipsis fired first and dropped the
   *   extension -- the one thing `formatFileName` is here to keep. The
   *   middle truncator was dead code on the surface it was added for.
   * - In the strip the column is ~240/280 and 28 characters truncated names
   *   that fit, on the surface whose whole job (FR77) is identifying a file
   *   well enough to jump to it.
   *
   * So the cap is sized to the column: ~7px per character at 14. These are
   * ESTIMATES of glyph width, not measurements -- the row still carries
   * `numberOfLines={1}` as the real backstop, and the owner's walk (AC 14)
   * is what confirms the extension survives at 320. The full, untruncated
   * name is always in `accessibilityLabel`, so nothing is lost to a reader
   * who cannot see the column at all.
   */
  nameMaxChars: number;
};

/**
 * One file row: the request's own 40pt thumbnail, the file's name, its size.
 *
 * ⚠️ THE ROW CARRIES THE ACCESSIBLE NAME AND THE THUMBNAIL IS HIDDEN FROM THE
 * TREE (4.2 AC 14). Labelling both announces the same file twice. The
 * component has no prop for hiding itself, and RNTL hides a11y-hidden nodes,
 * so the wrapper here is what does it -- exactly as the deleted tile did for
 * its own `<Image>`.
 *
 * ⚠️ `onLongPress` IS FORWARDED AND THAT IS LOAD-BEARING ON NATIVE (4.2 C3).
 * A row is a pressable CHILD, so its press does not bubble to the bubble's own
 * long-press; without the forwarding a files-only message is the one message
 * on native with no way to reach the actions chip. It is forwarded for EVERY
 * file now, photographs included, which was not true of the tile's own list.
 *
 * ⚠️ EVERY ROW OPENS SOMETHING (owner + UX consult, 2026-09-14). One
 * interaction for every attachment: an image opens the viewer, a `.pptx` opens
 * the metadata dialog showing what it is and a Download. Briefly a file was
 * inert instead, which is a dead end in a bubble with no visible Download
 * button. (In the FILES STRIP the one interaction is a jump to the message,
 * Story 7.6; the row does not know which surface it is on, and the caller's
 * `accessibilityLabel` is what says so to a screen reader.)
 */
export const AttachmentRow = ({
  file,
  testID,
  onPress,
  onLongPress,
  style,
  accessibilityLabel,
  nameMaxChars,
}: RowProps) => {
  const label =
    accessibilityLabel ??
    `${extensionLabel(file)}, ${file.name}, ${formatFileSize(file.sizeBytes)}`;
  const content = (
    <>
      <View
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      >
        <AttachmentThumbnail
          size="xsmall"
          // ⚠️ `source`, NEVER `attachment` (D1a, AC 3). See the file header.
          // `thumbnailUri` is absent for an image the server could not decode
          // (an iPhone HEIC), and the component then draws the type glyph --
          // not a spinner, not an error dot, not an empty box (AC 4).
          source={{ uri: file.thumbnailUri, mimeType: file.mimeType }}
          // C3: the component's own box is `#f5f5f5` under an `#e0e0e0` edge,
          // which measures 1.02:1 / 1.19:1 against the other-side bubble --
          // invisible. White under `borderPanel` is 1.22/1.11 and 1.44/1.58,
          // visible on BOTH bubbles, which is why one value serves both sides.
          containerStyle={styles.thumbnailBox}
        />
      </View>
      <View style={styles.text}>
        {/* `formatFileName` is the house truncator and keeps the extension on
            both platforms, where RNW's `ellipsizeMode="middle"` does not -- but
            only while its cap is NARROWER than what `numberOfLines={1}` would
            cut. That is the caller's to size; see {@link RowProps.nameMaxChars}. */}
        <Text style={styles.name} numberOfLines={1}>
          {formatFileName(file.name, nameMaxChars)}
        </Text>
        <Text style={styles.size}>{formatFileSize(file.sizeBytes)}</Text>
      </View>
    </>
  );
  /*
   * ⚠️ NOTHING TO DO ON EITHER PRESS IS CONTENT, NOT A BUTTON (#1069 offline
   * state: the bubble's rows while the device is offline). A disabled
   * `button` is announced as "dimmed, button", a control that exists and
   * refuses; this row is the file's name and size, read as text.
   */
  if (!onPress && !onLongPress) {
    return (
      <View
        testID={testID}
        style={[styles.row, style]}
        accessible
        accessibilityLabel={label}
      >
        {content}
      </View>
    );
  }
  return (
    <TouchableOpacity
      testID={testID}
      style={[styles.row, style]}
      onPress={onPress}
      onLongPress={onLongPress}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      {content}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  /**
   * ⚠️ NO FILL AND NO BORDER: THE BUBBLE IS THE GROUND (#1069 Story 8.5, C4).
   * That is what retired 8.4's `onMutedGround` prop rather than renaming it --
   * a row with nothing in it cannot be invisible against its own bubble, which
   * was D7's whole purpose. The one tone left is the thumbnail box, and it is
   * white on both sides (see {@link thumbnailBox}).
   *
   * ⚠️ `minHeight` IS AN EXPLICIT FLOOR, NOT A RESTATEMENT OF THE ARITHMETIC
   * (AC 5). 40 + 4 vertical is 48, comfortably over the 44 minimum -- but an
   * icon-less error state must not be allowed to collapse the tap target, and
   * there is no second wrapper here to hold one.
   *
   * `borderRadius` is for the web focus ring and the press highlight, not for
   * an edge: there is no `borderWidth` to draw one.
   */
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    minHeight: spacing.minimumTapTarget,
    borderRadius: 4,
  },
  /**
   * The thumbnail's dressing, applied to the icon box AND the image box by
   * `AttachmentThumbnail`'s `containerStyle` (C3). `borderPanel` rather than
   * `borderPanelSoft`: soft measures 1.21:1 on the muted ground and this edge
   * is the only boundary the box has.
   */
  thumbnailBox: {
    backgroundColor: colors.backgroundPrimary,
    borderColor: colors.borderPanel,
  },
  /**
   * ⚠️ `minWidth: 0` IS NOT DECORATION. Without it a long name overflows its
   * flex parent on react-native-web instead of truncating -- the flex-basis
   * `auto` default takes the text's own width as the minimum.
   */
  text: { flex: 1, minWidth: 0 },
  /**
   * ⚠️ 14 AND 11 ARE LITERALS, AND THAT IS SAID OUT LOUD (the convention the
   * chip this replaced already followed). 14 is the thread's own body size
   * (`MessageRow`'s `msgBody`), which is the point: a file's name reads at the
   * size of the message it arrived on. `fontSize.body` is 15 and
   * `fontSize.formLabel` is 14 -- but calling a file name a form label would
   * be a lie the next reader has to undo, and there is no 14 token that MEANS
   * this. 11 matches the panel's metadata ramp (`MessageRow`'s group line).
   */
  name: {
    fontSize: 14,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
  },
  /**
   * 11, on its own line under the name. `textSecondary` measures 4.70 / 5.16
   * on the two bubbles; `#999`, which the request's own metadata uses, would
   * be 2.33 / 2.56 -- so this follows the panel's text ramp rather than
   * Supporting Documents' colours.
   */
  size: {
    fontSize: 11,
    fontWeight: fontWeight.regular,
    color: colors.textSecondary,
  },
});
